import React, { useEffect, useState } from 'react'
import { Field, Form, withFormik } from 'formik'
import _set from 'lodash/set'
import _get from 'lodash/get'
import { withRouter } from 'react-router'
import Button from '../../_library/Button'
import { combineValidators, isEmailValidator, requiredValidator } from '../../../_common/core/validation'
import { PasswordField } from '../../formik/Fields/PasswordField'
import { RadioGroupField, SimpleField } from '../../formik/Fields'
import { movedToCreateBrand } from '../../../_common/core/gtag'

const CreateAccountForm = props => {
  const { isSubmitting, isValid, setFieldValue, values, submitForm } = props
  const [showAuthCode, setShowAuthCode] = useState(false)
  const [authCodeRequested, setAuthCodeRequested] = useState(false)
  const [requestingCode, setRequestingCode] = useState(false)
  const [authCodeError, setAuthCodeError] = useState('')
  
  const handleGoBack = () => {
    props.history.goBack()
  }

  const isBuyOrResellSelected = values.best_describe === '3'
  
  const handleContinue = async (e) => {
    e.preventDefault()
    
    // First time - request auth code
    setRequestingCode(true)
    setAuthCodeError('')
    
    try {
      // Use dedicated auth code endpoint
      await props.onRequestAuthCode({
        data: {
          attributes: {
            ...values,
            orderId: null // We don't have an order yet
          }
        }
      })
      setAuthCodeRequested(true)
    } catch (error) {
      console.error('Error requesting auth code:', error)
      // Extract error message from response if available
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message
      
      if (error.response?.status === 429) {
        setAuthCodeError('Rate limit exceeded. Please wait before requesting another code.')
      } else if (error.response?.status === 422) {
        setAuthCodeError('Validation error. Please check your form data.')
      } else if (errorMessage) {
        setAuthCodeError(errorMessage)
      } else {
        setAuthCodeError('Failed to send authorization code. Please try again or contact support.')
      }
      
      // Ensure button is re-enabled after error
      setRequestingCode(false)
    } finally {
      // Always show the auth code field after attempting to request code
      setShowAuthCode(true)
      setRequestingCode(false)
    }
  }
  
  const handleCreateAccount = async (e) => {
    e.preventDefault()
    
    // Submit form with auth code for account creation using Formik's submitForm
    // This will trigger the handleSubmit function which contains the navigation logic
    setAuthCodeError('')
    try {
      const data = await props.onSubmit({ ...values, authCode: values.authCode })
      
      // Navigate to create-brand page after successful account creation
      if (data) {
        localStorage.setItem(
          'createAccountState',
          JSON.stringify({
            ...values,
            skipCreateAccount: true,
            goBack: true,
          }),
        )
        props.history.push('/welcome/create-brand', {
          email: values.email,
          password: values.password,
          lastName: values.lastName,
          firstName: values.firstName,
          confirmPassword: values.confirmPassword,
          best_describe: values.best_describe,
          skipCreateAccount: props?.skipCreateAccount,
          referral: props.savedState?.referral,
          user_id: data?.data?.id,
        })
        movedToCreateBrand()
      }
    } catch (error) {
      console.error('Error creating account:', error)
      setAuthCodeError('Failed to create account. Please try again.')
    }
  }
  
  const handleRequestNewCode = async (e) => {
    e.preventDefault()
    setRequestingCode(true)
    setAuthCodeError('')
    
    // Clear the auth code field before requesting new code
    setFieldValue('authCode', '')
    
    try {
      // Use dedicated auth code endpoint
      await props.onRequestAuthCode({
        data: {
          attributes: {
            ...values,
            orderId: null // We don't have an order yet
          }
        }
      })
      setAuthCodeRequested(true)
    } catch (error) {
      console.error('Error requesting new auth code:', error)
      if (error.response?.status === 429) {
        setAuthCodeError('Rate limit exceeded. Please wait before requesting another code.')
      } else if (error.response?.status === 422) {
        setAuthCodeError('Validation error. Please check your form data.')
      } else {
        setAuthCodeError('Failed to send authorization code. Please try again or contact support.')
      }
    } finally {
      setRequestingCode(false)
    }
  }

  return (
    <Form className="margin-horizontal-25 account-form-container">
      <div className="title-form">Create your account</div>
      <Field
        type="firstName"
        name="firstName"
        label="First Name"
        component={SimpleField}
        formclassname="field-wrapper first-name-input"
        validate={requiredValidator()}
        touchedOnBlur
      />
      <Field
        type="lastName"
        name="lastName"
        label="Last Name"
        component={SimpleField}
        formclassname="field-wrapper responsive-input"
        validate={requiredValidator()}
        touchedOnBlur
      />
      <Field
        type="email"
        name="email"
        label="Email"
        component={SimpleField}
        formclassname="field-wrapper margin-top-24"
        validate={combineValidators(requiredValidator(), isEmailValidator('Invalid'))}
        touchedOnBlur
      />
      <Field
        type="password"
        name="password"
        label="Password"
        component={PasswordField}
        className="margin-top-24"
        validate={props?.skipCreateAccount ? undefined : requiredValidator()}
      />
      <Field
        type="password"
        name="confirmPassword"
        label="Confirm Password"
        component={PasswordField}
        className="responsive-input"
        validate={props?.skipCreateAccount ? undefined : requiredValidator()}
      />
      <div className="field-wrapper margin-top-24">
        <label className="label-best-describe">What best describes you?</label>
        <Field
          type="best_describe"
          name={'best_describe'}
          component={RadioGroupField}
          validate={requiredValidator()}
          groups={[
            {
              label: 'I want to Buy or Resell Tickets',
              value: '3',
            },
            {
              label: 'Event Organizer/Manager/Marketing',
              value: '1',
            },
            {
              label: 'Venue Owner/Manager/Marketing',
              value: '2',
            },
          ]}
          classNames={{
            container: 'radio-group-container single-column',
            container_item: 'radio-group-item',
            component: '',
          }}
        />
        {isBuyOrResellSelected ? (
          <div className="error-container">
            <p>
              This sign-up form is only for event organizers or venue owners. Please go to
              <a href="http://www.ticketfairy.com" target="_blank" rel="noopener noreferrer">
                {' '}
                www.ticketfairy.com{' '}
              </a>
              to buy or resell tickets.
            </p>
          </div>
        ) : null}
      </div>
      
      {showAuthCode && (
        <div className="field-wrapper margin-top-24">
          <Field
            type="text"
            name="authCode"
            label="Enter 6-character authorization code"
            component={SimpleField}
            formclassname="field-wrapper"
            validate={requiredValidator()}
            maxLength="6"
            placeholder="XXXXXX"
            touchedOnBlur
          />
          {authCodeRequested && !authCodeError && (
            <p className="help-text margin-top-8">
              An authorization code has been sent to your email address.
            </p>
          )}
          {authCodeError && (
            <p className="error-text margin-top-8" style={{color: '#d9534f'}}>
              {authCodeError}
            </p>
          )}
        </div>
      )}
      <div className="row">
            <p className="help-text margin-top-20">
          To create an account, we need to validate your email first.
        </p>
      </div>
      <div className="buttons-wrapper">
        <div className="btn-toolbar btn-toolbar-right">
          <Button
            type="button"
            className="btn btn-default btn-shadow"
            disabled={requestingCode || !isValid || isBuyOrResellSelected}
            onClick={showAuthCode ? handleRequestNewCode : handleContinue}
          >
            {showAuthCode ? 'Request New Code' : 'Request Code'}
            {requestingCode && <>&nbsp;<i className="fa fa-circle-o-notch fa-spin" /></>}
          </Button>
          <Button
            type="button"
            className={!isValid || isBuyOrResellSelected || !showAuthCode || !values.authCode || values.authCode.length !== 6 ? 'submit-disabled' : 'submit-button-login'}
            disabled={!isValid || isBuyOrResellSelected || !showAuthCode || !values.authCode || values.authCode.length !== 6}
            loading={isSubmitting}
            onClick={handleCreateAccount}
          >
            Create Account
          </Button>
        </div>
      </div>
    </Form>
  )
}

const CreateAccountFormFormik = withFormik({
  mapPropsToValues: props => {
    const searchParams = new URLSearchParams(window.location.search)
    const queryEmail = searchParams.get('email') || ''
    const stateEmail = props?.location?.state?.email || ''
    return {
      email: (props.email ?? stateEmail) || queryEmail,
      password: props?.skipCreateAccount && !props.goBack ? '' : props.password ?? '',
      lastName: props.lastName ?? '',
      firstName: props.firstName ?? '',
      confirmPassword: props?.skipCreateAccount && !props.goBack ? '' : props.confirmPassword ?? '',
      best_describe: props.location?.state?.best_describe ?? '',
      authCode: '',
    }
  },

  validate: (values, props) => {
    const errors = {}
    const password = _get(values, 'password')
    const confirmPassword = _get(values, 'confirmPassword')
    if (props?.skipCreateAccount && password === '' && confirmPassword === '') {
      return errors
    }
    if (confirmPassword !== password) {
      _set(errors, 'confirmPassword', 'Please enter the same password again')
    }

    if (password && password.length < 6) {
      _set(errors, 'password', 'Password must be at least 6 characters')
    }

    return errors
  },

  handleSubmit: async (
    { email, password, lastName, firstName, confirmPassword, best_describe, authCode, requestAuthCode },
    { props, setSubmitting },
  ) => {
    try {
      const newPassword = props?.skipCreateAccount && password === '' ? undefined : password
      const newConfirmPassword =
        props?.skipCreateAccount && confirmPassword === '' ? undefined : confirmPassword
      const user = {
        email,
        password: newPassword,
        lastName,
        firstName,
        confirmPassword: newConfirmPassword,
        best_describe,
      }
      
      // Add authCode to user data if provided
      if (authCode) {
        user.authCode = authCode
      }
      
      // Add requestAuthCode flag if this is a code request
      if (requestAuthCode) {
        user.requestAuthCode = true
      }
      
      const data = await props.onSubmit(user)
      
      // Only proceed with redirect if we actually created the account (not just requested auth code)
      if (!requestAuthCode && data) {
        localStorage.setItem(
          'createAccountState',
          JSON.stringify({
            ...user,
            skipCreateAccount: true,
            goBack: true,
          }),
        )
        setSubmitting(false)
        props.history.push('/welcome/create-brand', {
          email,
          password,
          lastName,
          firstName,
          confirmPassword,
          best_describe,
          skipCreateAccount: props?.skipCreateAccount,
          referral: props.savedState.referral,
          user_id: data?.data?.id,
        })
        movedToCreateBrand()
      } else {
        setSubmitting(false)
      }
    } catch (e) {
      setSubmitting(false)
      throw e
    }
  },

  displayName: 'CreateAccountForm',
})(CreateAccountForm)

const CreateAccountFormRouter = withRouter(CreateAccountFormFormik)

export default CreateAccountFormRouter
